/**
 * Shift viewing and editing for the two hierarchy screens.
 *
 * Both `/configuration/subordinate` (the org chart) and `/employee/subordinate`
 * (My Team) read the same endpoint and offer the same edit dialog; they differ
 * only in how the shift is drawn. Keeping the hook, the dialog and the colour
 * scale here means the two screens cannot drift into showing different shifts
 * for the same person on the same day.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { DialogModal } from "../ui/dialogModal";
import { Textarea } from "../ui/input";
import { HIERARCHY_SHIFT } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "./ToastMsg";
import { formatTimeRangeToAMPM } from "./CommonFunctions";
import { cn } from "../../utils/tailwindUtils";
import { CalendarClock, CalendarDays, Moon, RotateCcw, UserRound } from "lucide-react";

export const SHIFT_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Palette for colour-by-shift. Indexed by the shift's position in the legend so
 * the same shift keeps the same colour across both screens and across renders.
 * Deliberately not derived from the shift name — a hash would reshuffle every
 * colour the moment a shift is renamed.
 */
const SHIFT_COLORS = [
    { bg: "#e0f2fe", text: "#075985", ring: "#0ea5e9" }, // sky
    { bg: "#ede9fe", text: "#5b21b6", ring: "#8b5cf6" }, // violet
    { bg: "#dcfce7", text: "#166534", ring: "#22c55e" }, // green
    { bg: "#fef3c7", text: "#92400e", ring: "#f59e0b" }, // amber
    { bg: "#ffe4e6", text: "#9f1239", ring: "#f43f5e" }, // rose
    { bg: "#cffafe", text: "#155e75", ring: "#06b6d4" }, // cyan
    { bg: "#f3e8ff", text: "#6b21a8", ring: "#a855f7" }, // purple
    { bg: "#fee2e2", text: "#991b1b", ring: "#ef4444" }, // red
];

const NEUTRAL = { bg: "#f3f4f6", text: "#4b5563", ring: "#9ca3af" };
const PENDING = { bg: "#fef3c7", text: "#92400e", ring: "#f59e0b" };

/** Colour for one shift id, stable for the life of the legend. */
export const colorForShift = (shiftId, shifts) => {
    if (!shiftId) return NEUTRAL;
    const index = (shifts || []).findIndex((s) => s.id === shiftId);
    if (index < 0) return NEUTRAL;
    return SHIFT_COLORS[index % SHIFT_COLORS.length];
};

/**
 * What to show in a chip for a resolved day, in priority order. A day that is
 * off for a known reason reads as that reason; only an unexplained empty day is
 * "Not set", which is the thing a planner needs to notice.
 */
export const describeDay = (day) => {
    if (!day) return { label: "—", tone: NEUTRAL, title: "No data" };
    if (day.is_holiday) return { label: "Holiday", tone: NEUTRAL, title: "Company holiday" };
    if (day.is_weekoff) return { label: "Week off", tone: NEUTRAL, title: "Week off" };
    if (day.is_leave) {
        return { label: day.leave_label || "Leave", tone: NEUTRAL, title: day.leave_label || "On leave" };
    }
    if (!day.shift_id) {
        return { label: "Not set", tone: PENDING, title: "No shift assigned for this day" };
    }
    const timings = day.shift_timings_from && day.shift_timings_to
        ? ` (${formatTimeRangeToAMPM(day.shift_timings_from, day.shift_timings_to)})`
        : "";
    const sourceNote = {
        override: "Changed for this date",
        roster: "From the published roster",
        default: "Standing shift",
    }[day.source] || "";
    return {
        label: day.shift_name || "Shift",
        title: `${day.shift_name || "Shift"}${timings}${sourceNote ? ` · ${sourceNote}` : ""}`,
        isNight: !!day.is_night_shift,
        isOverride: day.source === "override",
    };
};

/**
 * Fetch the caller's hierarchy shifts for one date.
 *
 * Failure is deliberately quiet. This endpoint sits behind the Roster
 * permission, so a manager who can see the org chart but was never granted
 * Roster gets a 403 — that is a correct answer, not an error, and toasting it
 * on every page load would be noise. The screens simply render no chips.
 */
export const useHierarchyShifts = (date) => {
    const { fetch } = useApiMethods();
    const [state, setState] = useState({
        byEmployee: {},
        shifts: [],
        featureEnabled: false,
        canEditAny: false,
        loading: false,
    });

    const dateKey = date ? dayjs(date).format(SHIFT_DATE_FORMAT) : null;

    const load = useCallback(async (signal) => {
        if (!dateKey) return;
        setState((prev) => ({ ...prev, loading: true }));
        const response = await fetch(HIERARCHY_SHIFT, { date: dateKey }, null, signal);
        if (response?.status === "cancelled") return;

        if (response?.status !== "success") {
            setState({
                byEmployee: {}, shifts: [], featureEnabled: false,
                canEditAny: false, loading: false,
            });
            return;
        }

        const data = response.data || {};
        const byEmployee = {};
        (data.employees || []).forEach((row) => {
            byEmployee[row.employee?.id] = { day: row.day, canEdit: !!row.can_edit };
        });
        setState({
            byEmployee,
            shifts: data.shifts || [],
            featureEnabled: !!data.feature_enabled,
            canEditAny: !!data.can_edit_any,
            loading: false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateKey]);

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    return { ...state, refresh: () => load() };
};

const SCOPE_OPTIONS = [
    { value: "date", label: "This date only", hint: "Overrides the roster for one day" },
    { value: "onward", label: "From this date onward", hint: "Up to 14 days" },
    { value: "default", label: "Change standing shift", hint: "Does not change days already recorded" },
];

/**
 * Assign / clear one employee's shift.
 *
 * `day` is the currently resolved day for that employee, used to prefill and to
 * decide whether "Clear override" is offered — it only makes sense when there
 * is an override to clear.
 */
export const ShiftAssignDialog = ({ open, onClose, employee, date, day, shifts, onSaved }) => {
    const { update } = useApiMethods();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            date: dayjs(date),
            shift: day?.shift_id || null,
            scope: "date",
            through_date: null,
            is_weekoff: !!day?.is_weekoff,
            reason: "",
        });
        setSaving(false);
    }, [open, date, day]);

    const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const shiftOptions = useMemo(
        () => (shifts || []).map((s) => ({
            value: s.id,
            label: s.shift_timings_from
                ? `${s.name} (${formatTimeRangeToAMPM(s.shift_timings_from, s.shift_timings_to)})`
                : s.name,
        })),
        [shifts],
    );

    const submit = async (payload, successFallback) => {
        setSaving(true);
        const response = await update(HIERARCHY_SHIFT, payload);
        setSaving(false);

        if (response?.status === "success") {
            ToastMsg("success", response.message || successFallback);
            // The recompute reports anything it declined to change (a penalty
            // under appeal, an HR-finalised short duty). Surfacing it is the
            // difference between "saved" and "saved, but read this".
            (response.data?.warnings || []).forEach((warning) => ToastMsg("warning", warning));
            onSaved?.();
            onClose?.();
        } else {
            const errors = response?.errors;
            const firstError = errors && Object.values(errors)[0];
            ToastMsg("error", firstError || response?.message || "Failed to update shift");
        }
    };

    const handleSave = () => {
        if (!form.is_weekoff && !form.shift) {
            ToastMsg("error", "Please select a shift or mark the day as week off");
            return;
        }
        if (form.scope === "onward" && !form.through_date) {
            ToastMsg("error", "Please choose the last date this applies to");
            return;
        }
        submit({
            employee: employee?.id,
            date: dayjs(form.date).format(SHIFT_DATE_FORMAT),
            shift: form.is_weekoff ? null : form.shift,
            is_weekoff: !!form.is_weekoff,
            scope: form.scope,
            through_date: form.scope === "onward" && form.through_date
                ? dayjs(form.through_date).format(SHIFT_DATE_FORMAT)
                : undefined,
            reason: form.reason || undefined,
        }, "Shift updated successfully");
    };

    const handleClear = () => submit({
        employee: employee?.id,
        date: dayjs(form.date).format(SHIFT_DATE_FORMAT),
        clear: true,
        reason: form.reason || undefined,
    }, "Shift override cleared");

    const employeeName = employee?.employee_name
        || [employee?.first_name, employee?.last_name].filter(Boolean).join(" ")
        || "Employee";

    const currentDesc = describeDay(day);
    const currentTime =
        day?.shift_timings_from && day?.shift_timings_to
            ? formatTimeRangeToAMPM(day.shift_timings_from, day.shift_timings_to)
            : null;

    const popupContainer = () => document.body;

    const datePickerProps = {
        format: "DD-MM-YYYY",
        allowClear: false,
        disabled: saving,
        size: "middle",
        className: "w-full !h-10",
        getPopupContainer: popupContainer,
        styles: { popup: { zIndex: 2100 } },
        suffixIcon: <CalendarDays className="h-4 w-4 text-sidebar-primary" />,
    };

    return (
        <DialogModal
            open={open}
            onClose={onClose}
            onSave={handleSave}
            title={`Shift assignment`}
            saveLabel="Save shift"
            loading={saving}
            width="520px"
            editMode
        >
            <div className="flex flex-col gap-4 -mt-1">
                {/* Employee + current assignment */}
                <div className="rounded-xl border border-sidebar-primary/20 bg-gradient-to-br from-sidebar-primary/5 to-white dark:from-sidebar-primary/10 dark:to-gray-800/80 px-4 py-3">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sidebar-primary/15 text-sidebar-primary">
                            <UserRound className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {employeeName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Currently:{" "}
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                    {currentDesc.label}
                                    {currentTime ? ` · ${currentTime}` : ""}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Date — primary interactive control */}
                <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/30 p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <CalendarDays className="h-4 w-4 text-sidebar-primary" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                            Effective date
                        </span>
                        <span className="text-red-500 text-xs">*</span>
                    </div>
                    <DatePicker
                        {...datePickerProps}
                        value={form.date || null}
                        onChange={(value) => set("date", value)}
                        placeholder="Pick a date"
                    />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                        Click the field or calendar icon to choose the day this change applies to.
                    </p>
                </section>

                {/* Week off vs shift */}
                <section className="flex flex-col gap-3">
                    <label
                        className={cn(
                            "flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors",
                            form.is_weekoff
                                ? "border-amber-300 bg-amber-50/80 dark:border-amber-700/50 dark:bg-amber-950/30"
                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                            saving && "opacity-60 pointer-events-none",
                        )}
                    >
                        <Checkbox
                            checked={!!form.is_weekoff}
                            onChange={(e) => set("is_weekoff", e.target.checked)}
                            disabled={saving}
                        />
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            Mark this day as week off
                        </span>
                    </label>

                    {!form.is_weekoff && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                Shift <span className="text-red-500">*</span>
                            </label>
                            <Select
                                value={form.shift || undefined}
                                onChange={(value) => set("shift", value)}
                                options={shiftOptions}
                                placeholder="Choose shift timing"
                                disabled={saving}
                                showSearch
                                optionFilterProp="label"
                                className="w-full"
                                size="middle"
                                getPopupContainer={popupContainer}
                                popupMatchSelectWidth
                            />
                        </div>
                    )}
                </section>

                {/* Scope — card radios */}
                <section>
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Applies to</p>
                    <div className="flex flex-col gap-2">
                        {SCOPE_OPTIONS.map((option) => {
                            const selected = form.scope === option.value;
                            return (
                                <label
                                    key={option.value}
                                    className={cn(
                                        "flex cursor-pointer items-start gap-3 rounded-xl border-2 px-3 py-2.5 transition-all",
                                        selected
                                            ? "border-sidebar-primary bg-sidebar-primary/10 shadow-sm"
                                            : "border-gray-200 dark:border-gray-700 hover:border-sidebar-primary/30",
                                        saving && "opacity-60 pointer-events-none",
                                    )}
                                >
                                    <input
                                        type="radio"
                                        name="shift-scope"
                                        value={option.value}
                                        checked={selected}
                                        onChange={() => set("scope", option.value)}
                                        disabled={saving}
                                        className="mt-1 accent-sidebar-primary"
                                    />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {option.label}
                                        </span>
                                        <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">
                                            {option.hint}
                                        </span>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </section>

                {form.scope === "onward" && (
                    <section className="rounded-xl border border-sidebar-primary/20 bg-sidebar-primary/5 p-3">
                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Through date <span className="text-red-500">*</span>
                        </label>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                            Last day this shift applies (within 14 days from the start date).
                        </p>
                        <DatePicker
                            {...datePickerProps}
                            value={form.through_date || null}
                            onChange={(value) => set("through_date", value)}
                            disabledDate={(current) => form.date && current && current < dayjs(form.date).startOf("day")}
                            placeholder="Pick end date"
                        />
                    </section>
                )}

                <section className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Reason <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <Textarea
                        rows={3}
                        value={form.reason || ""}
                        onChange={(e) => set("reason", e.target.value)}
                        placeholder="Why is the shift changing? Saved with the change for audit."
                        disabled={saving}
                        className="text-sm resize-none"
                    />
                </section>

                {day?.override_id ? (
                    <button
                        type="button"
                        onClick={handleClear}
                        disabled={saving}
                        className={cn(
                            "inline-flex items-center justify-center gap-2 w-full rounded-lg border border-amber-200 dark:border-amber-800",
                            "bg-amber-50/80 dark:bg-amber-950/30 px-3 py-2 text-xs font-semibold",
                            "text-amber-800 dark:text-amber-300",
                            "hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors",
                            saving && "opacity-50 cursor-not-allowed",
                        )}
                    >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Clear override — follow roster again
                    </button>
                ) : null}
            </div>
        </DialogModal>
    );
};

/** Inline chip for a resolved day — used by the My Team list. */
export const ShiftBadge = ({ day, shifts }) => {
    const described = describeDay(day);
    const tone = described.tone || colorForShift(day?.shift_id, shifts);
    return (
        <span
            title={described.title}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ background: tone.bg, color: tone.text, border: `1px solid ${tone.ring}` }}
        >
            {described.label}
            {described.isNight ? <span title="Night shift">🌙</span> : null}
            {described.isOverride ? <span title="Changed for this date">•</span> : null}
        </span>
    );
};

const SOURCE_LABEL = {
    override: "Override",
    roster: "Roster",
    default: "Standing",
};

/** Calendar action attached to shift cells — reads as a control, not decoration. */
const ShiftEditAction = ({ onEdit, dateLabel }) => {
    const title = dateLabel
        ? `Change shift for ${dateLabel}`
        : "Change shift for this date";

    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onEdit();
            }}
            title={title}
            aria-label={title}
            className={cn(
                "group/edit flex flex-col items-center justify-center gap-0.5 shrink-0 self-stretch",
                "min-w-[3.25rem] px-1.5 py-2",
                "border-l border-sidebar-primary/25",
                "bg-sidebar-primary/10",
                "hover:bg-sidebar-primary/15",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sidebar-primary",
                "transition-colors duration-150 active:bg-sidebar-primary/20",
                "cursor-pointer",
            )}
        >
            <CalendarClock
                className={cn(
                    "h-4 w-4 text-sidebar-primary",
                    "transition-transform duration-150 group-hover/edit:scale-110",
                )}
                aria-hidden
            />
            <span className="text-[9px] font-bold uppercase tracking-wide text-sidebar-primary leading-none">
                Change
            </span>
        </button>
    );
};

/**
 * Rich shift cell for hierarchy tables — name, timings, source, optional edit.
 * When editable, shift info and the calendar action share one card so the
 * control is obvious in dense tables.
 */
export const ShiftTableCell = ({ day, shifts, canEdit, onEdit, dateLabel }) => {
    const described = describeDay(day);
    const tone = described.tone || colorForShift(day?.shift_id, shifts);
    const timeLabel =
        day?.shift_timings_from && day?.shift_timings_to
            ? formatTimeRangeToAMPM(day.shift_timings_from, day.shift_timings_to)
            : null;
    const sourceLabel = day?.shift_id ? SOURCE_LABEL[day?.source] : null;
    const compact =
        !day?.shift_id || day?.is_holiday || day?.is_weekoff || day?.is_leave;
    const showEdit = canEdit && onEdit;

    const shiftBody = compact ? (
        <div className="flex flex-1 min-w-0 items-center px-2 py-1.5">
            <ShiftBadge day={day} shifts={shifts} />
        </div>
    ) : (
        <div
            title={described.title}
            className="flex flex-1 min-w-0 items-stretch gap-2 px-2 py-1.5"
        >
            <div
                className="w-1 shrink-0 rounded-full self-stretch min-h-[2rem]"
                style={{ background: tone.ring }}
            />
            <div className="min-w-0 flex-1 leading-tight">
                <div className="flex items-center gap-1">
                    <span
                        className="text-[11px] font-semibold truncate"
                        style={{ color: tone.text }}
                    >
                        {described.label}
                    </span>
                    {described.isNight ? (
                        <Moon className="h-3 w-3 shrink-0 opacity-80" style={{ color: tone.text }} />
                    ) : null}
                </div>
                {timeLabel ? (
                    <p className="text-[10px] font-medium text-gray-600 dark:text-gray-300 tabular-nums mt-0.5">
                        {timeLabel}
                    </p>
                ) : null}
                {sourceLabel ? (
                    <p
                        className="text-[9px] uppercase tracking-wider font-medium mt-0.5 opacity-75"
                        style={{ color: tone.text }}
                    >
                        {sourceLabel}
                        {described.isOverride ? " · changed" : ""}
                    </p>
                ) : null}
            </div>
        </div>
    );

    if (!showEdit) {
        if (compact) {
            return <ShiftBadge day={day} shifts={shifts} />;
        }
        return (
            <div
                title={described.title}
                className="flex max-w-[240px] items-stretch rounded-lg border shadow-sm"
                style={{
                    background: tone.bg,
                    borderColor: `${tone.ring}55`,
                }}
            >
                {shiftBody}
            </div>
        );
    }

    return (
        <div
            className={cn(
                "flex max-w-[280px] items-stretch overflow-hidden rounded-lg border shadow-sm",
                "ring-0 hover:ring-2 hover:ring-sidebar-primary/35",
                "transition-shadow duration-150",
            )}
            style={
                compact
                    ? { borderColor: "hsl(var(--sidebar-primary) / 0.25)" }
                    : { background: tone.bg, borderColor: `${tone.ring}55` }
            }
        >
            {shiftBody}
            <ShiftEditAction onEdit={onEdit} dateLabel={dateLabel} />
        </div>
    );
};
